import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormikWrapper,
  FormTextField as TextField,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  userUpdate,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_REASON_UNKNOWN,
  setOperationStatus,
} from '@uncinc/react-drupal-core';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';
import * as Yup from 'yup';

import { useSelector } from 'react-redux';
import { FormikHelpers } from 'formik';
import { ROUTE_TYPE_GUIDED_SELF_HELP } from '../../../config/constants';
import { Button, CoreLink, PasswordField } from '../../../components';
import Page from '../../../containers/Page';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import useOperationStatus from '../../../hooks/useOperationStatus';
import useToast from '../../../hooks/useToast';

import './style.scss';
import useTypedDispatch from '../../../hooks/useTypedDispatch';

const PageNursePractitionerChangeEmail: React.FC = () => {
  const { t } = useTranslation();
  const { buildClass } = useClasses('PageNursePractitionerChangeEmail');
  const { account } = useRoutes(ROUTE_TYPE_GUIDED_SELF_HELP);
  const dispatch = useTypedDispatch();
  const operationStatus = useOperationStatus('user', 'update');
  const toast = useToast();

  useEffect(() => {
    if (operationStatus === UI_OPERATION_STATUS_SUCCESS) {
      toast.show(t('pages.nurse_practitioner_change_email.success_message'), 'success');
    }

    if (operationStatus === UI_OPERATION_STATUS_ERROR) {
      toast.show(t('pages.nurse_practitioner_change_email.error_message'), 'error');
    }

    if (operationStatus !== UI_OPERATION_STATUS_REQUEST) {
      // Consume the operation status so the toast won't show again.
      dispatch(setOperationStatus('user', 'update', null, UI_OPERATION_STATUS_REASON_UNKNOWN));
    }
  }, [operationStatus, dispatch, t, toast]);

  const validationSchema = Yup.object({
    newEmail: Yup
      .string()
      .required(t('pages.nurse_practitioner_change_email.fields.new_email.validation.required'))
      .email(t('pages.nurse_practitioner_change_email.fields.new_email.validation.email')),
    newEmailConfirm: Yup
      .string()
      .oneOf([Yup.ref('newEmail'), null], t('pages.nurse_practitioner_change_email.fields.new_email_confirm.validation.no_match'))
      .required(t('pages.nurse_practitioner_change_email.fields.new_email_confirm.validation.required')),
    password: Yup
      .string()
      .required(t('pages.nurse_practitioner_change_email.fields.password.validation.required')),
  });

  type FormValues = Yup.InferType<typeof validationSchema>;

  const initialValues: FormValues = {
    newEmail: '',
    newEmailConfirm: '',
    password: '',
  };

  const currentUserId = useSelector<ImmutableRootState<number>, number>(
    (state) => state.getIn(['login', 'uid']),
  );

  const onSubmit = (values: FormValues, formik: FormikHelpers<FormValues>) => {
    const { newEmail, password } = values;
    formik.setSubmitting(false);
    dispatch(userUpdate(currentUserId, {
      mail: [{
        value: newEmail,
      }],
      pass: [{
        existing: password,
      }],
    }));
  };

  return (
    <Page className={buildClass()} title={t('pages.nurse_practitioner_change_email.title')}>
      <div className="col-md-8 offset-md-2 col-lg-6 col-lg-1 py-8 py-lg-12">
        <header className={buildClass('header')}>
          <CoreLink
            to={account.base}
            title={t('buttons.back')}
            className="icon-before icon-chevron icon-orientation-left"
          />
        </header>

        <main className={buildClass('main')}>
          <h1 className={buildClass('title')}>
            {t('pages.nurse_practitioner_change_email.title')}
          </h1>
          <div className={buildClass('form-wrapper')}>
            <FormikWrapper
              initialValues={initialValues}
              validationSchema={validationSchema}
              validateOnBlur={false}
              validateOnChange={false}
              onSubmit={onSubmit}
            >
              <TextField
                title={t('pages.nurse_practitioner_change_email.fields.new_email.label')}
                fieldName="newEmail"
              />

              <TextField
                title={t('pages.nurse_practitioner_change_email.fields.new_email_confirm.label')}
                fieldName="newEmailConfirm"
              />

              <PasswordField
                title={t('pages.nurse_practitioner_change_email.fields.password.label')}
                fieldName="password"
              />

              <Button
                htmlType="submit"
                buttonState={operationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
              >
                {t('pages.nurse_practitioner_change_email.submit')}
              </Button>
            </FormikWrapper>
          </div>
        </main>
      </div>
    </Page>
  );
};

export default PageNursePractitionerChangeEmail;
